import React from 'react';
import { Navigate } from 'react-router-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Tag,
  LogOut,
  ChevronRight,
  Menu,
  X
} from 'lucide-react';
import { useAuth } from '../../store/authContext';
import { Button } from '../ui/button';
import { useState } from 'react';
import brandLogo from '@/assets/logo.png';

export const AdminLayout = ({ children }) => {
  const { isAuthenticated, isLoading, admin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const navItems = [
    { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/admin/porudžbine', label: 'Porudžbine', icon: ShoppingBag },
    { to: '/admin/proizvodi', label: 'Proizvodi', icon: Package },
    { to: '/admin/kategorije', label: 'Kategorije', icon: Tag },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-5 border-b border-primary-foreground/10">
        <img
          src={brandLogo}
          alt="Fazlić admin"
          className="h-14 w-auto object-contain"
        />
        <p className="text-primary-foreground/50 text-xs font-ui mt-2">Admin Panel</p>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map(item => {
          const Icon = item.icon;
          const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-200 ${
                isActive
                  ? 'bg-primary-foreground/10 text-primary-foreground'
                  : 'text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground/90'
              }`}
            >
              <Icon size={18} />
              <span className="font-ui text-sm font-medium">{item.label}</span>
              {isActive && <ChevronRight size={14} className="ml-auto" />}
            </Link>
          );
        })}
      </nav>

      {/* Admin info & logout */}
      <div className="p-4 border-t border-primary-foreground/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-8 w-8 rounded-full bg-gold/20 flex items-center justify-center">
            <span className="text-gold text-sm font-semibold font-ui">{admin?.name?.charAt(0) || 'A'}</span>
          </div>
          <div>
            <p className="text-primary-foreground text-sm font-ui font-medium">{admin?.name}</p>
            <p className="text-primary-foreground/40 text-xs font-ui">{admin?.username}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-primary-foreground/60 hover:text-red-400 hover:bg-red-400/10 transition-colors duration-200 text-sm font-ui"
        >
          <LogOut size={16} />
          Odjavi se
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-muted">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-60 admin-sidebar fixed left-0 top-0 bottom-0 z-40">
        <SidebarContent />
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-foreground/40 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={`lg:hidden fixed left-0 top-0 bottom-0 w-64 admin-sidebar z-50 transition-transform duration-300 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <SidebarContent />
      </aside>

      {/* Main content */}
      <div className="flex-1 lg:ml-60 flex flex-col min-h-screen">
        {/* Mobile header */}
        <header className="lg:hidden flex items-center gap-4 px-4 py-3 bg-card border-b border-border sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <Menu size={20} className="text-foreground" />
          </button>
          <span className="font-serif text-base font-semibold text-foreground">Admin Panel</span>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};
